import { Outlet, Link, useLocation } from "react-router-dom";
import { Inbox, LayoutDashboard, Menu, LogOut } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@hub/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@hub/ui/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@hub/ui/components/ui/avatar";
import { ThemeToggle } from "@hub/ui/components/ThemeToggle";
import { SessionTimeoutDialog } from "./SessionTimeoutDialog";
import { appConfig } from "@hub/shared/config";

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: { name: string; href: string }[];
}

function isNavActive(pathname: string, href: string): boolean {
  if (href === "/queue") {
    return pathname === "/queue" || pathname.startsWith("/queue/");
  }
  return pathname === href || pathname.startsWith(href + "/");
}

export function WorkbenchLayout() {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout, validateSession, extendSession } = useAuth();

  const isManager = user?.role === "admin" || user?.role === "operator";

  const navItems: NavItem[] = [
    { name: "My Queue", href: "/queue", icon: Inbox },
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: LayoutDashboard,
      ...(isManager
        ? {
            children: [
              { name: "My Stats", href: "/dashboard" },
              { name: "Team SQDIP", href: "/dashboard/team" },
            ],
          }
        : {}),
    },
  ];

  const currentPage = navItems.find((n) => isNavActive(location.pathname, n.href))?.name || "Workbench";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-coronis-navy-800 dark:to-coronis-navy-600">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 bg-coronis-navy-900 dark:bg-white/5 dark:backdrop-blur-sm dark:border-r dark:border-white/10 transition-all duration-300 ${
          sidebarCollapsed ? "w-16" : "w-64"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 bg-coronis-navy-800 dark:bg-white/5">
            {!sidebarCollapsed && (
              <span className="text-xl font-bold text-white">Workbench</span>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 text-coronis-navy-300 hover:text-white rounded-md hover:bg-white/10 transition-colors"
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isNavActive(location.pathname, item.href);

              return (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`
                      flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all
                      ${
                        isActive
                          ? "bg-coronis-blue-600 text-white shadow-[0_0_12px_rgba(59,130,246,0.3)]"
                          : "text-coronis-navy-200 hover:bg-white/10 hover:text-white"
                      }
                    `}
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!sidebarCollapsed && (
                      <span className="ml-3">{item.name}</span>
                    )}
                  </Link>
                  {item.children && isActive && !sidebarCollapsed && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`
                            block px-3 py-1.5 rounded text-xs font-medium transition-colors
                            ${location.pathname === child.href
                              ? "text-white"
                              : "text-gray-400 hover:text-gray-200"
                            }
                          `}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div
        className={`transition-all duration-300 ${
          sidebarCollapsed ? "pl-16" : "pl-64"
        }`}
      >
        {/* Header */}
        <header className="h-16 bg-white dark:bg-white/5 dark:backdrop-blur-sm dark:border-b dark:border-white/10 shadow-xs">
          <div className="flex items-center justify-between h-full px-6">
            <h1 className="text-lg font-semibold text-gray-800 dark:text-white">
              {currentPage}
            </h1>
            <div className="flex items-center space-x-4">
              <ThemeToggle />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full focus:outline-hidden focus:ring-2 focus:ring-coronis-blue-500">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-coronis-navy-600 text-white text-sm">
                        {user?.displayName?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.displayName}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => logout()}
                    className="text-red-600"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
      </div>

      <SessionTimeoutDialog
        validateSession={validateSession}
        extendSession={extendSession}
        warningSeconds={appConfig.session.warningSeconds}
        checkIntervalSeconds={appConfig.session.checkIntervalSeconds}
      />
    </div>
  );
}
